import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { track } from '../../analytics';
import { type ExpandedLayout, getNewConversationText } from '../../config/constants';
import { useChatLanguage, useChatTheme } from '../../context/ChatThemeContext';
import CrossIcon from '../CrossIcon';
import ArrowLeftIcon from '../icons/ArrowLeftIcon';
import ChatPlusIcon from '../icons/ChatPlusIcon';
import CopyIcon from '../icons/CopyIcon';
import ExpandCenterIcon from '../icons/ExpandCenterIcon';
import MenuIcon from '../icons/MenuIcon';
import PanelRightIcon from '../icons/PanelRightIcon';

// How long the "Copied" acknowledgement replaces the menu item label.
const COPIED_FEEDBACK_MS = 1600;

// Ties the menu panel to its trigger for assistive tech. Static because only one
// expanded chat header is mounted at a time.
const MENU_BUTTON_ID = 'inboundx-conversation-menu-button';

const MUTED = 'var(--rw-icon-muted)';

/**
 * Client brand colours are arbitrary, and a light one used as text on the white
 * menu is unreadable. Falls back to the muted token above ~65% relative
 * luminance. Only #rgb/#rrggbb are inspected; anything else (a CSS var, a named
 * colour) is passed through unchanged rather than guessed at.
 */
export const readableAccent = (color: string | undefined): string => {
    if (!color) return MUTED;

    const hex = color.trim().replace('#', '');
    const full =
        hex.length === 3
            ? hex
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : hex;
    if (full.length !== 6 || !/^[0-9a-f]{6}$/i.test(full)) return color;

    const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(full.slice(i, i + 2), 16) / 255);
    // Rec. 709 relative luminance — good enough to catch "too pale to read".
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 0.65 ? MUTED : color;
};

interface ExpandedChatHeaderProps {
    onMinimize: () => void;
    onToggleLayout?: () => void;
    showLayoutToggle?: boolean;
    viewLayout?: ExpandedLayout;
    showBackButton?: boolean;
    onBack?: () => void;
    showExitCopilot?: boolean;
    onExitCopilotMode?: () => void;
    /** IX-4266: menu only appears once there is a conversation to act on. */
    showConversationMenu?: boolean;
    onResetConversation?: () => void;
    onCopyTranscript?: () => Promise<boolean> | boolean;
}

const ExpandedChatHeader: React.FC<ExpandedChatHeaderProps> = ({
    onMinimize,
    viewLayout = 'expanded',
    onToggleLayout,
    showLayoutToggle = false,
    showBackButton = false,
    onBack,
    showExitCopilot = false,
    onExitCopilotMode,
    showConversationMenu = false,
    onResetConversation,
    onCopyTranscript,
}) => {
    const language = useChatLanguage();
    const theme = useChatTheme();
    // Design 1d tints the active menu row with the accent; use the site's own
    // primary colour rather than the mock's hard-coded blue. Brand colours are
    // client-supplied and some are near-white (#F5F5F5), which as *text* on a
    // white menu is invisible — fall back to the muted token when the colour is
    // too light to read.
    const accent = readableAccent(theme?.primaryColor);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [justCopied, setJustCopied] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const menuButtonRef = useRef<HTMLButtonElement | null>(null);

    // Click-outside and Escape both close. Without these the panel is a trap on
    // a client's page, where we don't own the surrounding DOM.
    useEffect(() => {
        if (!isMenuOpen) return;

        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            // The widget mounts inside a shadow root (widget/src/main.tsx), so by
            // the time an event reaches a document-level listener its `target` has
            // been retargeted to the shadow host. `contains(target)` is then false
            // even for a click on this very menu, which closed it on mousedown and
            // unmounted the item before its click could fire. composedPath() keeps
            // the real path across the shadow boundary.
            const path = event.composedPath?.() ?? [];
            const isInside = path.length
                ? path.some((node) => node === menuRef.current || node === menuButtonRef.current)
                : (() => {
                      const target = event.target as Node | null;
                      return Boolean(
                          target &&
                              (menuRef.current?.contains(target) ||
                                  menuButtonRef.current?.contains(target))
                      );
                  })();

            if (isInside) return;
            setIsMenuOpen(false);
        };
        // role="menu" promises arrow-key navigation; without it, Tab from the
        // open menu lands on Minimize before either item.
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMenuOpen(false);
                menuButtonRef.current?.focus();
                return;
            }

            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

            const items = Array.from(
                menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []
            );
            if (!items.length) return;

            event.preventDefault();
            // document.activeElement reports the shadow host, not the focused
            // node inside it, so resolve focus against the menu's own root.
            const root = menuRef.current?.getRootNode() as Document | ShadowRoot | undefined;
            const active = root?.activeElement ?? null;
            const current = items.findIndex((item) => item === active);
            const next =
                event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? items.length - 1
                      : event.key === 'ArrowDown'
                        ? (current + 1) % items.length
                        : (current - 1 + items.length) % items.length;
            items[next]?.focus();
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMenuOpen]);

    // Move focus into the menu on open, per the ARIA menu-button pattern.
    useEffect(() => {
        if (!isMenuOpen) return;
        menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    }, [isMenuOpen]);

    useEffect(() => {
        if (!justCopied) return;
        const timer = setTimeout(() => setJustCopied(false), COPIED_FEEDBACK_MS);
        return () => clearTimeout(timer);
    }, [justCopied]);

    const handleToggleMenu = useCallback(() => {
        setIsMenuOpen((open) => {
            if (!open) {
                track('conversation_menu_opened', {}, 'user_action');
            }
            return !open;
        });
    }, []);

    const handleNewConversation = useCallback(() => {
        // Opening the menu and picking a labelled row is already two deliberate
        // steps, so this doesn't carry a second confirmation.
        track('new_conversation_clicked', {}, 'user_action');
        setIsMenuOpen(false);
        onResetConversation?.();
    }, [onResetConversation]);

    const handleCopyTranscript = useCallback(async () => {
        const copied = await onCopyTranscript?.();
        track('conversation_transcript_copied', { succeeded: Boolean(copied) }, 'user_action');
        if (copied) {
            setJustCopied(true);
        }
    }, [onCopyTranscript]);

    const menuItemClass =
        'flex items-center gap-[9px] w-full text-left border-none cursor-pointer ' +
        'rounded-lg px-2.5 py-2.5 text-sm bg-transparent transition-colors duration-150';

    return (
        <div
            className="inboundx-expanded-chat-header flex flex-col w-full"
            style={{ borderBottom: '1px solid var(--rw-border-subtle)' }}
        >
            <div className="relative z-30 flex justify-between items-center py-3 px-3 gap-1">
                <div className="flex items-center gap-1">
                    {showBackButton && onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            title="Back to conversation"
                            className="w-5 h-5 border-none bg-transparent cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                        >
                            <ArrowLeftIcon width={20} height={20} color="#656565" />
                        </button>
                    )}
                    {showLayoutToggle && onToggleLayout && (
                        <button
                            type="button"
                            onClick={onToggleLayout}
                            title={
                                viewLayout === 'expanded'
                                    ? 'Switch to side panel'
                                    : 'Switch to expanded view'
                            }
                            className="inboundx-layout-toggle-button w-5 h-5 border-none bg-transparent cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                        >
                            {viewLayout === 'expanded' ? (
                                <PanelRightIcon
                                    width={20}
                                    height={20}
                                    color="var(--rw-icon-muted)"
                                />
                            ) : (
                                <ExpandCenterIcon
                                    width={20}
                                    height={20}
                                    color="var(--rw-icon-muted)"
                                />
                            )}
                        </button>
                    )}
                    {showExitCopilot && onExitCopilotMode && (
                        <button
                            type="button"
                            onClick={onExitCopilotMode}
                            title="Switch to expanded view"
                            className="w-5 h-5 border-none bg-transparent cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                        >
                            <ExpandCenterIcon width={20} height={20} color="var(--rw-icon-muted)" />
                        </button>
                    )}
                </div>

                {/* Design 1d: conversation actions collapse into a menu beside
                    the close, so the header stays two controls wide however
                    many actions get added later. */}
                <div className="flex items-center gap-3">
                    {showConversationMenu && (
                        <button
                            ref={menuButtonRef}
                            id={MENU_BUTTON_ID}
                            type="button"
                            onClick={handleToggleMenu}
                            aria-label={getNewConversationText('MENU', language)}
                            aria-haspopup="menu"
                            aria-expanded={isMenuOpen}
                            className="inboundx-conversation-menu-button w-5 h-5 border-none bg-transparent cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                        >
                            <MenuIcon
                                width={20}
                                height={20}
                                color={isMenuOpen ? accent : 'var(--rw-icon-muted)'}
                            />
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={onMinimize}
                        title="Minimize chat"
                        className="inboundx-expanded-chat-minimize-button w-5 h-5 border-none bg-none cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                    >
                        <CrossIcon width={20} height={20} color="var(--rw-icon-muted)" />
                    </button>
                </div>

                {showConversationMenu && isMenuOpen && (
                    <div
                        ref={menuRef}
                        role="menu"
                        // Labelled by the button rather than repeating the string:
                        // duplicating it gives the trigger and the panel one
                        // ambiguous accessible name.
                        aria-labelledby={MENU_BUTTON_ID}
                        className="absolute right-3 top-full z-50 mt-1 flex w-56 flex-col gap-0.5 rounded-xl border bg-white p-1.5"
                        style={{
                            borderColor: 'var(--rw-border-subtle)',
                            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.14)',
                        }}
                    >
                        {onResetConversation && (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={handleNewConversation}
                                className={`${menuItemClass} font-medium`}
                                style={{ color: accent, backgroundColor: `${accent}0D` }}
                            >
                                <ChatPlusIcon width={16} height={16} color={accent} />
                                <span>{getNewConversationText('LABEL', language)}</span>
                            </button>
                        )}
                        {onCopyTranscript && (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={handleCopyTranscript}
                                className={`${menuItemClass} hover:bg-neutral-100`}
                                style={{ color: 'var(--rw-icon-muted)' }}
                            >
                                <CopyIcon width={16} height={16} color="var(--rw-icon-muted)" />
                                <span>
                                    {justCopied
                                        ? getNewConversationText('COPIED', language)
                                        : getNewConversationText('COPY_TRANSCRIPT', language)}
                                </span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExpandedChatHeader;
